<template>
  <v-container class="d-flex justify-center" style="max-width: unset; justify-content: flex-start;">
    <v-card style="min-width: 1400px; max-width: 1400px;">
      <v-card-title class="d-flex justify-start align-center">
        <span class="mr-2 text-h8">Positions</span>
        <v-btn color="secondary" icon variant="text" size="large" title="Refresh Positions" class="ml-2"
          @click="refreshPositions">
          <v-icon size="32" class="font-weight-bold">
            mdi-refresh
          </v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text>
        <v-progress-linear v-if="loading" indeterminate color="primary" />
  
        <!-- Confirmation dialog -->
        <GlobalDialog v-model="confirmDelete" title="Confirm Delete"
          message="Are you sure you want to delete this position?" @ok="confirmDeleteAction" @cancel="cancelDelete" />
  
        <!-- Positions table -->
        <v-data-table :key="page" :headers="headersWithActions" :items="positions" :items-per-page="itemsPerPage"
          :server-items-length="totalPositions" class="elevation-1" show-pagination="false" overflow-x-auto>
          <!-- Row template -->
          <template #item="{ item }">
            <tr>
              <td>
                <div class="d-flex align-center">
                  <v-img :src="item.application === 'BREEZE' ? '/icici.png' : '/zerodha.png'" :alt="item.application"
                    :max-width="item.application === 'BREEZE' ? 50 : 25"
                    :max-height="item.application === 'BREEZE' ? 50 : 25" cover />
                </div>
              </td>
              <td>{{ item.stock_code }}</td>
              <td>{{ item.action }}</td>
              <td>{{ item.product_type }}</td>
              <td>
                <v-text-field v-if="isEditing(item)" v-model="item.quantity" style="min-width: 80px;" density="compact"
                  hide-details />
                <span v-else>{{ item.quantity }}</span>
              </td>
              <td>
                <v-text-field v-if="isEditing(item)" v-model.number="item.average_price" density="compact" hide-details
                  style="min-width: 80px;" />
                <span v-else>{{ item.average_price }}</span>
              </td>
              <td>
                <v-text-field v-if="isEditing(item)" v-model.number="item.ltp" density="compact" hide-details
                  style="min-width: 80px;" />
                <span v-else>{{ item.ltp }}</span>
              </td>
              <td>
                <v-text-field v-if="isEditing(item)" v-model.number="item.strike_price" density="compact" hide-details
                  style="min-width: 80px;" />
                <span v-else>{{ item.strike_price }}</span>
              </td>
              <td>{{ item.right }}</td>
              <td>
                <v-text-field v-if="isEditing(item)" v-model="item.expiry_date" density="compact" hide-details
                  style="min-width: 160px;" />
                <span v-else>{{ formatDate(item.expiry_date) }}</span>
              </td>
              <td>
                <v-select v-if="isEditing(item)" v-model="item.positionStatus" :items="['OPEN', 'CLOSED']"
                  density="compact" hide-details style="min-width: 100px" />
                <span v-else>{{ item.positionStatus }}</span>
              </td>
              <td>
                <v-text-field v-if="isEditing(item)" v-model="item.exit_price"
                  :rules="[item.positionStatus === 'CLOSED' ? v => !!v || 'Required' : () => true]" density="compact"
                  hide-details style="min-width: 80px;" :readonly="item.positionStatus === 'OPEN'"
                  :clearable="item.positionStatus === 'OPEN'"
                  :placeholder="item.positionStatus === 'CLOSED' ? 'Required' : '—'" />
                <span v-else>{{ item.exit_price }}</span>
              </td>
              <!-- Actions -->
              <td class="d-flex gap-2">
                <v-icon size="20" color="primary" @click="toggleEdit(item)">
                  {{ isEditing(item) ? 'mdi-check' : 'mdi-pencil' }}
                </v-icon>
  
                <v-icon v-if="isEditing(item)" size="20" color="grey" @click="cancelEdit(item)">
                  mdi-close
                </v-icon>
  
                <v-icon size="20" color="error" @click="triggerDelete(item)">
                  mdi-delete
                </v-icon>
              </td>
  
            </tr>
          </template>
        </v-data-table>
  
        <v-divider class="my-4" />
  
        <v-row align="center" justify="space-between">
          <v-col cols="12" md="12" class="text-md-right">
            <span class="font-weight-medium">Page {{ page }} of {{ totalPages }}</span>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
export default {
  data() {
    return {
      loading: true,
      page: 1,
      itemsPerPage: 10,
      totalPositions: 0,
      positions: [],
      editingMap: {},
      originalDataMap: {},
      confirmDelete: false,
      deleteTarget: null,
      headers: [
        { title: 'App', key: 'application', width: 80 },
        { title: 'Stock', key: 'stock_code', width: 100 },
        { title: 'Action', key: 'action', width: 80 },
        { title: 'Type', key: 'product_type', width: 100 },
        { title: 'Qty', key: 'quantity', width: 80 },
        { title: 'Avg Price', key: 'average_price', width: 100 },
        { title: 'LTP', key: 'ltp', width: 80 },
        { title: 'Strike', key: 'strike_price', width: 100 },
        { title: 'Right', key: 'right', width: 80 },
        { title: 'Expiry', key: 'expiry_date', width: 120 },
        { title: 'Status', key: 'positionStatus', width: 100 },
        { title: 'Exit Price', key: 'exitPrice', width: 100 },
      ],
    };
  },
  computed: {
    headersWithActions() {
      const isAnyEditing = Object.keys(this.editingMap).length > 0;
      return [
        ...this.headers.map(h => ({
          ...h,
          sortable: isAnyEditing ? false : true
        })),
        { title: 'Actions', key: 'actions', sortable: false, width: 100 }
      ];
    },
    totalPages() {
      return Math.ceil(this.totalPositions / this.itemsPerPage);
    },
  },
  mounted() {
    this.fetchPositions();
  },
  methods: {
    async fetchPositions() {
      this.loading = true;
      try {
        const res = await this.$store.dispatch('positions/getPositions', {
          offset: (this.page - 1) * this.itemsPerPage,
          count: this.itemsPerPage,
        });
        this.positions = res.positionList || [];
        this.totalPositions = res.totalCount || this.positions.length || 0;
      } catch (error) {
        console.error('Failed to fetch positions:', error);
      } finally {
        this.loading = false;
      }
    },
    nextPage() {
      if (this.page < this.totalPages) {
        this.page++;
        this.fetchPositions();
      }
    },
    prevPage() {
      if (this.page > 1) {
        this.page--;
        this.fetchPositions();
      }
    },
    async refreshPositions() {
      this.loading = true;
      try {
        await this.$store.dispatch('positions/refreshPositions');
        this.page = 1;
        await this.fetchPositions();
      } catch (error) {
        console.error('Failed to refresh:', error);
      } finally {
        this.loading = false;
      }
    },
    isEditing(item) {
      return !!this.editingMap[item.optionPositionId];
    },
    toggleEdit(item) {
      this.loading = true;
      const id = item.optionPositionId;
      try {
        if (this.isEditing(item)) {
          const payload = {
            optionPositionId: id,
            positionInfoToUpdate: {
              expiry_date: item.expiry_date,
              strike_price: item.strike_price,
              quantity: item.quantity,
              average_price: item.average_price,
              ltp: item.ltp,
              exitPrice: item.exit_price,
              positionStatus: item.positionStatus,
            }
          };
          this.$store.dispatch('positions/updatePosition', payload).then(() => {
            this.fetchPositions();
          });
          delete this.editingMap[id];
          delete this.originalDataMap[id];
        } else {
          this.editingMap[id] = true;
          this.originalDataMap[id] = JSON.parse(JSON.stringify(item));
        }
      } catch (error) {
        console.error('Failed to update position:', error);
      } finally {
        this.loading = false;
      }
    },
    triggerDelete(item) {
      this.deleteTarget = item;
      this.confirmDelete = true;
    },
    cancelDelete() {
      this.confirmDelete = false;
      this.deleteTarget = null;
    },
    confirmDeleteAction() {
      if (this.deleteTarget) {
        this.removeOrderFromPosition(this.deleteTarget.optionPositionId);
      }
      this.cancelDelete();
    },
    removeOrderFromPosition(id) {
      console.log('📦 remove Order from position in action', id);
      this.$store.dispatch('orders/removeOrderFromPosition', { optionPositionsId: id });
    },
    formatDate(dateStr) {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
    },
    cancelEdit(item) {
      const id = item.optionPositionId;
      if (this.originalDataMap[id]) {
        Object.assign(item, this.originalDataMap[id]); // Restore original
      }
      delete this.editingMap[id];
      delete this.originalDataMap[id];
    },
  },
};
</script>
<style scoped>
/* Alternative way to target headers */
:deep(th) {
  font-weight: bold !important;
  font-size: 14px !important;
  background-color: #fbeee6 !important;
}

:deep(td),
:deep(th) {
  padding: 4px 6px !important;
  font-size: 13px !important;
}
.custom-header {
  background-color: #fbeee6;
}

</style>